import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { getScreens, type IScreensReadParams } from 'shared/src/api';
import { useFilterListAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/useFilterListAtom';
import { applyDeferredViewEventSelect } from './utils/applyDeferredViewEventSelect';

type SearchResultQueryParams = {
  screenName: string;
  query: string;
  nextToken?: string;
};

/**
 * 검색 결과 전용 무한 스크롤 쿼리 훅
 * - query 필수 (빈 문자열이면 비활성화)
 * - 검색어/필터 변경 시 항상 새로 요청 (staleTime: 0)
 * - select 파생 로직(VIEW 이벤트 지연 발화)은 applyDeferredViewEventSelect 참조
 */
export const useSearchResultQuery = ({
  screenName,
  query,
  nextToken,
}: SearchResultQueryParams) => {
  const {
    filterListAtomValue: { screen_name: _ignoredScreenName, from_screen: _ignoredFromScreen, ...restFilterListAtomValue },
  } = useFilterListAtom();

  const queryResult = useInfiniteQuery({
    queryKey: [
      'searchResult',
      { ...restFilterListAtomValue, nextToken, screenName, query },
    ],
    queryFn: ({ pageParam }) => {
      const requestParams = {
        ...restFilterListAtomValue,
        next_token: pageParam ?? null,
        screen: screenName,
        query,
      } as IScreensReadParams;
      return getScreens(requestParams);
    },
    enabled: query !== '',
    initialPageParam: nextToken ?? null,
    getNextPageParam: lastPage => {
      return lastPage.nextToken || undefined;
    },
    select: applyDeferredViewEventSelect,
    staleTime: 0,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });

  return queryResult;
};
